import { Component, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Location } from '@angular/common';
import { PanelModule } from 'primeng/panel';
import { ButtonModule } from 'primeng/button';
import { Textarea } from 'primeng/textarea';
import { MessageModule } from 'primeng/message';
import { LinkError } from '@apollo/client';
import { firstValueFrom } from 'rxjs';
import { MessageService } from 'src/app/services/message.service';
import { RoutingService } from 'src/app/services/routing.service';
import { SubtitleService } from 'src/app/services/subtitle.service';
import { UserService } from 'src/app/services/user.service';
import { RegisterUserAmazonNotificationGQL } from 'src/gql/generated';
import { gqlErrors } from 'src/app/utils/gql-errors';

@Component({
  selector: 'app-amazon-notification-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.sass'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, PanelModule, ButtonModule, Textarea, MessageModule],
})
export class AmazonNotificationRegisterComponent {
  asinInput = '';
  isLoading = false;
  messages: any[] = [];

  constructor(
    private location: Location,
    private routingService: RoutingService,
    private subtitleService: SubtitleService,
    private userService: UserService,
    private messageService: MessageService,
    private registerGQL: RegisterUserAmazonNotificationGQL,
  ) {
    this.routingService.loginRequired();
    this.userService.amazonNotifyRequired();
    this.subtitleService.setSubTitle('Amazon通知 - 手動登録');
  }

  async onSubmit() {
    this.messages = [];

    if (!this.asinInput.trim()) {
      this.messages = [{ severity: 'error', summary: 'エラー', detail: 'ASINを入力してください' }];
      return;
    }

    // ASINのバリデーションと整形
    const asins = this.validateAndFormatAsins(this.asinInput);

    if (asins.length === 0) {
      this.messages = [{ severity: 'error', summary: 'エラー', detail: '有効なASINが見つかりませんでした' }];
      return;
    }

    this.isLoading = true;

    try {
      const result = await firstValueFrom(this.registerGQL.mutate({ variables: { asins } }));

      // GraphQLエラーをチェック
      if (gqlErrors(result)?.length > 0) {
        console.error('GraphQL errors:', gqlErrors(result));

        // エラーメッセージを構築
        const errorMessages = gqlErrors(result).map((error) => {
          if (error.extensions?.code === 'E0001') {
            return 'サーバー内部エラーが発生しました。管理者にお問い合わせください。';
          }
          return error.message || '不明なエラーが発生しました。';
        });

        this.messages = [
          {
            severity: 'error',
            summary: 'エラー',
            detail: errorMessages.join(' '),
          },
        ];
        return;
      }

      // データがnullの場合もエラーとして扱う
      if (result.data === null) {
        this.messages = [
          {
            severity: 'error',
            summary: 'エラー',
            detail: '登録処理でエラーが発生しました。再度お試しください。',
          },
        ];
        return;
      }

      // 成功の場合
      this.messages = [
        {
          severity: 'success',
          summary: '成功',
          detail: `${asins.length}件のASINを登録しました`,
        },
      ];

      // 成功後、入力をクリア
      this.asinInput = '';
    } catch (error) {
      console.error('Registration error:', error);

      // ネットワークエラーやその他の例外
      let errorDetail = '登録に失敗しました。しばらく時間をおいて再度お試しください。';

      // GraphQL エラーは errorPolicy 'all' で結果の error に入って返るので、ここに来るのは通信エラーとそれ以外の例外だけ
      if (LinkError.is(error)) {
        errorDetail = 'ネットワークエラーが発生しました。インターネット接続を確認してください。';
      }

      this.messages = [
        {
          severity: 'error',
          summary: 'エラー',
          detail: errorDetail,
        },
      ];
    } finally {
      this.isLoading = false;
    }
  }

  private validateAndFormatAsins(input: string): string[] {
    const lines = input.split('\n');
    const validAsins: string[] = [];
    const asinRegex = /^[A-Z0-9]{10}$/;

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed) {
        if (asinRegex.test(trimmed)) {
          // 重複チェック
          if (!validAsins.includes(trimmed)) {
            validAsins.push(trimmed);
          }
        }
      }
    }

    return validAsins;
  }

  onClear() {
    this.asinInput = '';
    this.messages = [];
  }

  goBack() {
    this.location.back();
  }
}
